import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ProductPublic } from '../lib/supabase';
import { versionedImageUrl } from '../lib/imageUrl';
import './CatalogBrowse.css';

const kinds = ['Luminarias', 'Rieles', 'Fuentes de poder', 'Accesorios'];
const composeScales = [25, 35, 45, 55] as const;
const composePowerGuide: Record<number, string> = {
  25: '2–5W',
  35: '3–7W',
  45: '5–10W',
  55: '7–15W',
};

function kind(p: ProductPublic) {
  const leaves = (p.category_paths ?? []).map(x => x.split('/').at(-1)?.toLowerCase() ?? '');
  if (leaves.some(x => x.includes('accesorio'))) return 'Accesorios';
  if (leaves.some(x => x.includes('fuente'))) return 'Fuentes de poder';
  if (leaves.some(x => ['empotrado', 'sobrepuesto'].includes(x)) && (p.category_paths ?? []).some(x => x.startsWith('Riel Magnético'))) return 'Rieles';
  return 'Luminarias';
}
function photo(p?: ProductPublic) { return versionedImageUrl(p?.images?.find(x => x.is_thumbnail)?.url ?? p?.images?.[0]?.url); }
function composeScale(p: ProductPublic) {
  const value = Number(p.custom_fields?.scale_mm ?? p.name.match(/(?:Ø|\b)(25|35|45|55)\b/)?.[1] ?? 0);
  return composeScales.includes(value as typeof composeScales[number]) ? value : 0;
}

function ComposeCatalogProducts({ items, render }: { items: ProductPublic[]; render: (p: ProductPublic, i: number) => ReactNode }) {
  const [section, setSection] = useState<'modules' | 'bases'>('modules');
  const [scale, setScale] = useState<number | null>(null);
  const modules = items.filter(p => p.product_type === 'compose_module');
  const bases = items.filter(p => p.product_type === 'compose_base');
  const visible = section === 'modules' ? modules : bases;
  const scales = composeScales.filter(value => visible.some(p => composeScale(p) === value));
  const shownScales = scale && scales.includes(scale as typeof composeScales[number]) ? [scale] : scales;
  const sorted = (products: ProductPublic[]) => [...products].sort((a, b) => {
    const family = (a.custom_fields?.compose_family ?? '').localeCompare(b.custom_fields?.compose_family ?? '', 'es');
    if (family) return family;
    const watts = Number(a.custom_fields?.wattage_w ?? 0) - Number(b.custom_fields?.wattage_w ?? 0);
    return watts || a.name.localeCompare(b.name, 'es');
  });

  return <div className="catalog-browse compose-catalog">
    <section className="compose-catalog-intro">
      <div>
        <p className="compose-catalog-kicker">COMPOSE · BUILD YOUR LIGHT</p>
        <h2>Diseña el sistema completo.</h2>
        <p>Elige tamaño, base, módulos, acabado, temperatura y control en una sola configuración.</p>
      </div>
      <div className="compose-catalog-actions">
        <Link className="compose-catalog-primary" to="/compose#configurador">Abrir configurador COMPOSE</Link>
        <Link className="compose-catalog-secondary" to="/compose">Conocer el sistema</Link>
      </div>
    </section>

    <div className="cb-tabs compose-catalog-tabs" aria-label="Sección de COMPOSE">
      <button aria-pressed={section === 'modules'} onClick={() => setSection('modules')}>
        Luminarias <small>{modules.length}</small>
      </button>
      <button aria-pressed={section === 'bases'} onClick={() => setSection('bases')}>
        Bases y montajes <small>{bases.length}</small>
      </button>
    </div>

    <div className="compose-scale-nav" aria-label="Filtrar por tamaño">
      <span>Diámetro del sistema</span>
      <button aria-pressed={scale === null} onClick={() => setScale(null)}>Todos</button>
      {scales.map(value => <button key={value} aria-pressed={scale === value} onClick={() => setScale(value)}>Ø{value}</button>)}
    </div>

    {shownScales.map(value => {
      const products = sorted(visible.filter(p => composeScale(p) === value));
      return <section key={`${section}-${value}`} className="cb-group compose-scale-group">
        <header>
          <div>
            <p>{section === 'modules' ? 'Módulos de luz' : 'Bases compatibles'}</p>
            <h2>Ø{value} mm <small>{products.length}</small></h2>
          </div>
          <span>{section === 'modules' ? `${composePowerGuide[value]} · Elige la función de luz` : 'Selecciona formato, montaje y número de módulos'}</span>
        </header>
        <div className="product-grid" style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16}}>{products.map(render)}</div>
      </section>;
    })}
  </div>;
}

export function CatalogCategories({ items, path, selected, onToggle }: { items: ProductPublic[]; path: string; selected: string[]; onToggle: (path: string) => void }) {
  const depth = path ? path.split('/').length : 0;
  const children = [...new Set(items.flatMap(p => (p.category_paths ?? []).filter(x => !path || x.startsWith(path + '/')).map(x => x.split('/').slice(0, depth + 1).join('/'))))].filter(x => x && x !== path).sort((a,b) => { const rank = (x: string) => /accesorio/i.test(x) ? 3 : /fuente/i.test(x) ? 2 : /empotrado|sobrepuesto/i.test(x) ? 1 : 0; return rank(a)-rank(b) || a.localeCompare(b, 'es'); });
  if (!children.length) return null;
  return <div className="catalog-browse"><p className="cb-eyebrow">{path ? 'Filtra por subcategoría · puedes elegir varias' : 'Explora nuestras categorías'}</p><div className="cb-categories">{children.map(child => {
    const matches = items.filter(p => p.category_paths?.some(x => x === child || x.startsWith(child + '/')));
    const representative = matches.find(p => kind(p) === 'Luminarias' && photo(p)) ?? matches.find(p => photo(p));
    const content = <><img src={photo(representative)} alt="" /><span><strong>{child.split('/').at(-1)}</strong><small>{matches.length} productos</small></span></>;
    if (path) return <button type="button" key={child} aria-pressed={selected.includes(child)} onClick={() => onToggle(child)}>{content}<span className="cb-selection-mark" aria-hidden="true">{selected.includes(child) ? '−' : '+'}</span></button>;
    return <Link key={child} to={`/catalogo?path=${encodeURIComponent(child)}`}><img src={photo(representative)} alt="" /><span><strong>{child.split('/').at(-1)}</strong><small>{matches.length} productos</small></span></Link>;
  })}</div></div>;
}
export function CatalogProducts({ items, path, render }: { items: ProductPublic[]; path: string; render: (p: ProductPublic, i: number) => ReactNode }) {
  const [choice, setChoice] = useState('');
  if (path === 'COMPOSE') return <ComposeCatalogProducts items={items} render={render} />;
  const available = kinds.filter(k => items.some(p => kind(p) === k));
  const selected = available.includes(choice) ? choice : available[0];
  const visible = items.filter(p => kind(p) === selected);
  const groups = [...new Set(visible.map(p => {
    const paths = (p.category_paths ?? []).filter(x => !path || x === path || x.startsWith(path + '/'));
    return paths.sort((a,b) => b.split('/').length-a.split('/').length)[0]?.split('/').at(-1) ?? selected;
  }))].sort((a,b) => a.localeCompare(b, 'es'));
  const groupOf = (p: ProductPublic) => (p.category_paths ?? []).filter(x => !path || x === path || x.startsWith(path + '/')).sort((a,b) => b.split('/').length-a.split('/').length)[0]?.split('/').at(-1) ?? selected;
  if (!path || items.every(p => p.brand_code?.toLowerCase() === 'lutron')) return <div className="product-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16}}>{items.map(render)}</div>;
  return <div className="catalog-browse"><div className="cb-tabs" aria-label="Tipo de producto">{available.map(k => <button key={k} aria-pressed={selected === k} onClick={() => setChoice(k)}>{k} <small>{items.filter(p => kind(p) === k).length}</small></button>)}</div>{groups.map(group => <section key={group} className="cb-group"><h2>{group} <small>{visible.filter(p => groupOf(p) === group).length}</small></h2><div className="product-grid" style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:16}}>{visible.filter(p => groupOf(p) === group).map(render)}</div></section>)}</div>;
}
